
/**
 * Translates text to the target language using backend translation API
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language code (e.g., 'es', 'fr')
 * @param {string} sourceLang - Source language code (default: 'en')
 * @returns {Promise<string>} Translated text
 */
export const translateText = async (text, targetLang, sourceLang = 'en') => {
  try {
    if (!text || text.trim() === '') return text;

    const response = await fetch(`https://sbgbackendtest.onrender.com/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        targetLang,
        sourceLang
      })
    });

    if (!response.ok) {
      throw new Error(`Translation failed: ${response.status} ${response.statusText}`);
    }

    const { translatedText } = await response.json();
    return translatedText;

  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original text if translation fails
  }
};

/**
 * Translates a job object to the target language
 * @param {Object} job - Job object to translate
 * @param {string} targetLang - Target language code
 * @returns {Promise<Object>} Translated job object
 */
export const translateJob = async (job, targetLang) => {
  if (!job) return null;
  if (targetLang === 'en') return job; // No translation needed for English

  try {
    const translatedJob = { ...job };

    // Basic text fields to translate
    const textFields = [
      'title',
      'company',
      'description',
      'location',
      'employmentType',
      'workSchedule',
      'experienceLevel',
      'education',
      'jobType',
      'industry',
      'department',
      'benefits',
      'companyDescription',
      'responsibilities',
      'qualifications'
    ];

    // Translate all text fields
    for (const field of textFields) {
      if (job[field] && typeof job[field] === 'string') {
        translatedJob[field] = await translateText(job[field], targetLang);
        // Add small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Handle arrays (like requirements, skills, etc)
    const arrayFields = ['requirements', 'skills', 'tags', 'perks', 'technologies'];
    for (const field of arrayFields) {
      if (Array.isArray(job[field])) {
        translatedJob[field] = await Promise.all(
          job[field].map(async (item) => {
            if (typeof item === 'string') {
              const translated = await translateText(item, targetLang);
              // Add small delay to avoid rate limiting
              await new Promise(resolve => setTimeout(resolve, 100));
              return translated;
            }
            return item;
          })
        );
      }
    }

    // Handle nested objects (if any)
    const objectFields = ['contact', 'salary', 'workLocation'];
    for (const field of objectFields) {
      if (job[field] && typeof job[field] === 'object' && !Array.isArray(job[field])) {
        const nestedObj = {};
        for (const [key, value] of Object.entries(job[field])) {
          if (typeof value === 'string') {
            nestedObj[key] = await translateText(value, targetLang);
            // Add small delay to avoid rate limiting
            await new Promise(resolve => setTimeout(resolve, 100));
          } else {
            nestedObj[key] = value;
          }
        }
        translatedJob[field] = nestedObj;
      }
    }

    return translatedJob;
  } catch (error) {
    console.error('Job translation error:', error);
    return job; // Return original job if translation fails
  }
};
